import { useState, useEffect, useRef } from 'react'
import { Spinner } from '@heroui/react'
import { useAuthStore } from '../../store/useAuthStore'
import { checkSessionExpired } from '../../api/client'
import { ChartLoginPlaceholder, useChartHidden } from './shared/ChartAuthGate'
import { ChartDownloadButton } from './shared/ChartDownloadButton'


function parseApiStr(s) {
  return Date.UTC(+s.slice(0,4), +s.slice(4,6)-1, +s.slice(6,8), +s.slice(8,10), +s.slice(10,12))
}

function findNearest(data, ts) {
  let lo = 0, hi = data.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (data[mid][0] < ts) lo = mid + 1
    else hi = mid
  }
  if (lo > 0 && Math.abs(data[lo - 1][0] - ts) < Math.abs(data[lo][0] - ts)) lo--
  return { ts: data[lo][0], value: data[lo][1] }
}

const DAYS_CS = ['ne', 'po', 'út', 'st', 'čt', 'pá', 'so']

// ts je "posunutý epoch ms" = UTC epoch ms + tzOffsetMs, zobrazujeme přes getUTC* jako lokální čas
function formatTs(ts, dateOnly = false) {
  const d = new Date(ts)
  const day = DAYS_CS[d.getUTCDay()]
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const yyyy = d.getUTCFullYear()
  if (dateOnly) return `${day} ${dd}.${mm}.${yyyy}`
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const min = String(d.getUTCMinutes()).padStart(2, '0')
  return `${day} ${dd}.${mm}.${yyyy} ${hh}:${min}`
}

function tzOffsetMsFromIana(timezone, refMs) {
  // GMT+HH:MM nebo GMT+H formát (Java TimeZone.getID()) — parsujeme přímo
  const gmtMatch = timezone.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/)
  if (gmtMatch) {
    const sign = gmtMatch[1] === '+' ? 1 : -1
    const h = parseInt(gmtMatch[2], 10)
    const m = parseInt(gmtMatch[3] || '0', 10)
    return sign * (h * 60 + m) * 60000
  }
  // IANA timezone — použij Intl
  const refMsRounded = Math.round(refMs / 60000) * 60000
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const parts = Object.fromEntries(fmt.formatToParts(refMsRounded).filter(x => x.type !== 'literal').map(x => [x.type, x.value]))
  const localMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute)
  return localMs - refMsRounded
}

function ChartTooltip({ crosshair, plotArea, dateOnly }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: crosshair.tipX,
        top: plotArea.y,
        pointerEvents: 'none',
        zIndex: 10,
      }}
      className="bg-white border border-gray-200 rounded shadow-md text-xs min-w-[200px] max-w-[260px]"
    >
      <div className="px-2.5 py-1.5 border-b border-gray-100 font-medium text-gray-700">
        {formatTs(crosshair.ts, dateOnly)}
      </div>
      <div className="px-2.5 py-1.5 flex flex-col gap-0.5">
        {crosshair.values.map((s, i) => (
          <div key={`${s.id ?? s.label}-${i}`} className="flex items-center gap-1.5">
            <span
              style={{
                background: s.color ?? 'transparent',
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
              }}
            />
            <span className="text-gray-600 flex-1 truncate">{s.label}</span>
            <span className="text-gray-800 font-medium ml-1 whitespace-nowrap">
              {/* display = předformátovaný řetězec (derived řádky); jinak value + unit */}
              {s.display != null
                ? s.display
                : s.value == null || s.value === 'NaN' || (typeof s.value === 'number' && isNaN(s.value))
                  ? '—'
                  : `${s.value} ${s.unit}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Statické vykreslení grafu z už načteného JSON (data.png/jpg base64 nebo data.svg string).
// Žádný fetch — data dostane přes props. Sdíleno ChartFrame (neinteraktivní režim).
export function ChartStatic({ data }) {
  const { svg, png, jpg, meta } = data ?? {}
  const w = meta?.context?.width || 'auto'
  const h = meta?.context?.height || 'auto'
  const imgSrc = png ? `data:image/png;base64,${png}` : jpg ? `data:image/jpeg;base64,${jpg}` : null
  if (!svg && !imgSrc) return null

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {svg
        ? <div dangerouslySetInnerHTML={{ __html: svg }} />
        : <img src={imgSrc} alt="Graf" width={w} height={h} style={{ display: 'block', maxWidth: '100%' }} />
      }
      <ChartDownloadButton data={data} />
    </div>
  )
}

// Interaktivní vykreslení grafu (crosshair + tooltip) z už načteného JSON.
// Žádný fetch — data dostane přes props. Sdíleno ChartFrame (interaktivní režim).
//
// deriveTooltip — volitelný callback (nearest, { series, nearestTs }) => rows[],
//   kterým může volající přepsat/rozšířit řádky tooltipu (např. sranorm:
//   kumulativní úhrn + odchylka od kumulativního normálu). nearest = pole
//   { id, label, unit, color, value, data, nearestTs, ... } per série.
export function ChartViewInner({ data, gran, deriveTooltip }) {
  const [crosshair, setCrosshair] = useState(null)
  const wrapperRef = useRef(null)

  function handleMove(e) {
    if (!data?.meta) return
    const { series, context } = data.meta
    const formatMeta = data.svg ? data.meta.svg : data.png ? data.meta.png : data.jpg ? data.meta.jpg : data.meta.gif
    if (!formatMeta) return
    const { plotArea, domain } = formatMeta
    if (!domain || !domain.from || !domain.to) return

    const rect = wrapperRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left

    if (mouseX < plotArea.x || mouseX > plotArea.x + plotArea.width) {
      setCrosshair(null)
      return
    }

    const ratio = (mouseX - plotArea.x) / plotArea.width
    const ts = domain.from + ratio * (domain.to - domain.from)

    // domain.from/to jsou lokální čas jako epoch ms (JFreeChart DateAxis + timezone stanice)
    // series data jsou UTC epoch ms → tzOffsetMs = lokální - UTC
    // DLY data jsou vázaná na kalendářní den (půlnoc UTC) bez posunu podle timezone stanice
    const p = context?.period
    const tzOffsetMs = gran === 'dly'
      ? 0
      : p?.from && p?.fromUtc
        ? parseApiStr(p.from) - parseApiStr(p.fromUtc)
        : tzOffsetMsFromIana(context?.timezone || 'UTC', ts)

    const nearest = series
      .filter(s => s.data && s.data.length > 0)
      .map(s => { const n = findNearest(s.data, ts - tzOffsetMs); return { ...s, value: n.value, nearestTs: n.ts } })

    // nearestTs je UTC → přičteme offset → "posunutý" lokální čas pro formatTs (getUTC*)
    const nearestTs = nearest.length > 0 ? nearest[0].nearestTs + tzOffsetMs : ts

    // přepočítáme nearestTs zpět na pixel, aby crosshair čára seděla na datovém bodu
    // nearestTs je lokální čas (UTC + offset), domain je také v lokálním čase → přímý přepočet
    const nearestUtc = nearest.length > 0 ? nearest[0].nearestTs : ts - tzOffsetMs
    const nearestRatio = (nearestUtc + tzOffsetMs - domain.from) / (domain.to - domain.from)
    const nearestX = plotArea.x + nearestRatio * plotArea.width

    const tipX = nearestX < plotArea.x + plotArea.width - 230
      ? nearestX + 8
      : nearestX - 238

    let values = nearest
    if (deriveTooltip) {
      try {
        values = deriveTooltip(nearest, {
          series,
          nearestTs: nearest.length > 0 ? nearest[0].nearestTs : null,
          hoverTs: ts - tzOffsetMs,   // syrový čas pod kurzorem (UTC epoch), bez snapu
        })
      } catch (err) {
        console.error('[ChartViewInner] deriveTooltip selhal:', err)
        values = nearest
      }
    }

    setCrosshair({ x: nearestX, ts: nearestTs, values, tipX })
  }

  const { svg, png, jpg, meta } = data ?? {}
  const w = meta?.context?.width || 'auto'
  const h = meta?.context?.height || 'auto'
  const formatMeta = svg ? meta?.svg : png ? meta?.png : jpg ? meta?.jpg : meta?.gif
  const plotArea = formatMeta?.plotArea
  const hasCrosshair = formatMeta?.domain?.from && formatMeta?.domain?.to

  const imgSrc = png ? `data:image/png;base64,${png}` : jpg ? `data:image/jpeg;base64,${jpg}` : null

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: w, height: h, display: 'inline-block' }}>
      {svg
        ? <div dangerouslySetInnerHTML={{ __html: svg }} />
        : imgSrc
          ? <img src={imgSrc} alt="Graf" width={w} height={h} style={{ display: 'block' }} />
          : null
      }
      <ChartDownloadButton data={data} />
      {hasCrosshair && (
        <div
          style={{
            position: 'absolute',
            left: plotArea.x,
            top: plotArea.y,
            width: plotArea.width,
            height: plotArea.height,
            cursor: 'crosshair',
          }}
          onMouseMove={handleMove}
          onMouseLeave={() => setCrosshair(null)}
        />
      )}
      {crosshair && (
        <>
          <div style={{
            position: 'absolute',
            left: crosshair.x,
            top: plotArea.y,
            width: 1,
            height: plotArea.height,
            background: 'rgba(0,0,0,0.45)',
            pointerEvents: 'none',
          }} />
          <ChartTooltip
            crosshair={crosshair}
            plotArea={plotArea}
            dateOnly={gran === 'dly'}
          />
        </>
      )}
    </div>
  )
}

// Zpětně kompatibilní wrapper: fetch JSON grafu + ChartViewInner. Ponechán pro
// místa mimo graf-view, která importují ChartView přímo (WindRose*, apod.).
// Graf-view používají ChartFrame (vždy JSON+meta, kvůli meta.toFill).
export function ChartView({ url, gran }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const token = useAuthStore(s => s.token)
  const chartHidden = useChartHidden()

  useEffect(() => {
    if (chartHidden) return
    setLoading(true)
    setError(false)
    setData(null)

    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(url, { headers })
      .then(r => { if (!r.ok) { checkSessionExpired(r); throw new Error(`HTTP ${r.status}`) }; return r.json() })
      .then(d => { setData(d); setLoading(false) })
      .catch(e => { console.error('[ChartView] Chyba:', e); setError(true); setLoading(false) })
  }, [url, token, chartHidden])

  if (chartHidden) return <ChartLoginPlaceholder />
  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
      <Spinner size="sm" /> Načítání grafu…
    </div>
  )
  if (error) return <p className="text-red-500 text-sm py-4">Chyba při načítání grafu</p>

  return <ChartViewInner data={data} gran={gran} />
}
